/**
 * Split a person name into first / middle / last (pure — no I/O).
 * Keep in sync with backend/src/domain/identity/parsePersonName.js.
 */

function tokenizeName(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function tokensFromFullName(fullName) {
  const tokens = tokenizeName(fullName);
  if (tokens.length === 0) {
    return { firstName: '', middleName: '', lastName: '' };
  }
  if (tokens.length === 1) {
    return { firstName: tokens[0], middleName: '', lastName: '' };
  }
  if (tokens.length === 2) {
    return { firstName: tokens[0], middleName: '', lastName: tokens[1] };
  }
  return {
    firstName: tokens[0],
    middleName: tokens.slice(1, -1).join(' '),
    lastName: tokens[tokens.length - 1],
  };
}

/**
 * @param {{ firstName?: unknown, middleName?: unknown, lastName?: unknown, fullName?: unknown }} [input]
 * @returns {{ firstName: string, middleName: string, lastName: string, ambiguousTwoToken: boolean }}
 */
export function parsePersonName(input = {}) {
  const explicitFirst = String(input.firstName || '').trim();
  const explicitMiddle = String(input.middleName || '').trim();
  const explicitLast = String(input.lastName || '').trim();
  const fromFull = tokensFromFullName(input.fullName);
  const fullTokenCount = tokenizeName(input.fullName).length;

  let firstName = explicitFirst;
  let middleName = explicitMiddle;
  let lastName = explicitLast;

  if (fullTokenCount >= 3 && !explicitMiddle) {
    firstName = fromFull.firstName;
    middleName = fromFull.middleName;
    lastName = fromFull.lastName;
  } else if (!firstName && !lastName && fullTokenCount > 0) {
    firstName = fromFull.firstName;
    lastName = fromFull.lastName;
    if (!middleName) middleName = fromFull.middleName;
  } else {
    if (!firstName && fromFull.firstName) firstName = fromFull.firstName;
    if (!lastName && fromFull.lastName) lastName = fromFull.lastName;
    if (!middleName && fromFull.middleName) middleName = fromFull.middleName;
  }

  const ambiguousTwoToken = !explicitMiddle && fullTokenCount === 2;

  return { firstName, middleName, lastName, ambiguousTwoToken };
}

/**
 * @param {{ firstName?: unknown, middleName?: unknown, lastName?: unknown }} parts
 * @returns {string}
 */
export function composeFullName(parts = {}) {
  return [parts.firstName, parts.middleName, parts.lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
}

export { tokenizeName };
